// public/src/utils/printUtility.js
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import baseURL from '../baseURL';
import { resolveArticleMasterRate } from './articleMasterRate';

// Standard paper sizes in cm
const STANDARD_SIZES = {
  A4: { width: 21.0, height: 29.7 },
  LETTER: { width: 21.59, height: 27.94 },
  LEGAL: { width: 21.59, height: 35.56 },
  A3: { width: 29.7, height: 42.0 },
  A5: { width: 14.8, height: 21.0 }
};

/** Must match PledgePrintA5MaharashtraPhotoStandard / printer settings */
const PLEDGE_A5_MAHARASHTRA_VARIANT = 'A5_MAHARASHTRA_PHOTO_STANDARD';
/** Must match PledgePrintA5NoTear / printer settings */
const PLEDGE_A5_NO_TEAR_VARIANT = 'A5_PLEDGE_TICKET_NO_TEAR';
/** Must match PledgePrintA5WithTear / printer settings */
const PLEDGE_A5_WITH_TEAR_VARIANT = 'A5_PLEDGE_TICKET_WITH_TEAR';
/** Must match ReleasePrintA5MoneyLender / printer settings */
const RELEASE_A5_MONEY_LENDER_VARIANT = 'A5_RELEASE_MONEY_LENDER';

// Convert centimeters to microns (Electron custom pageSize uses microns)
const cmToMicrons = (cm) => Math.round(Number(cm) * 10000);

const getElectronPageSize = (paperSize, printerType, documentType = null, layoutTemplateVariant = null) => {
  // Dot matrix: continuous paper - don't enforce a page size via Electron.
  if (printerType === 'DOT_MATRIX') return undefined;
  if (!paperSize) return 'A4';

  if (paperSize.type === 'STANDARD') {
    const name = (paperSize.standardSize || 'A4').toUpperCase();
    // Pledge Book A3: 40cm x 22cm (length x width)
    if (documentType === 'pledgeBook' && name === 'A3') {
      return { width: cmToMicrons(22), height: cmToMicrons(40) };
    }
    // Pre-Auction List A3: portrait 22cm x 40cm
    if (documentType === 'preAuctionList' && name === 'A3') {
      return { width: cmToMicrons(22), height: cmToMicrons(40) };
    }
    // Auction Register A3: landscape 40cm x 22cm
    if (documentType === 'auctionRegister' && name === 'A3') {
      return { width: cmToMicrons(40), height: cmToMicrons(22) };
    }
    // Release: A5 money-lender = portrait 14.8×21 cm
    if (documentType === 'release') {
      if (name === 'A5' && layoutTemplateVariant === RELEASE_A5_MONEY_LENDER_VARIANT) {
        return { width: cmToMicrons(14.8), height: cmToMicrons(21.0) };
      }
      if (name === 'A5') return { width: cmToMicrons(14.8), height: cmToMicrons(21.0) };
      if (name === 'A4') return { width: cmToMicrons(21.0), height: cmToMicrons(29.7) };
    }
    // Pledge: A5 Maharashtra = landscape (21×14.8 cm); other A5/A4 = portrait
    if (documentType === 'pledge') {
      if (name === 'A5' && layoutTemplateVariant === PLEDGE_A5_MAHARASHTRA_VARIANT) {
        return { width: cmToMicrons(21.0), height: cmToMicrons(14.8) };
      }
      if (name === 'A5') return { width: cmToMicrons(14.8), height: cmToMicrons(21.0) };
      if (name === 'A4') return { width: cmToMicrons(21.0), height: cmToMicrons(29.7) };
    }
    if (name === 'LETTER') return 'Letter';
    if (name === 'LEGAL') return 'Legal';
    return name; // A4/A5/A3
  }

  if (paperSize.type === 'CUSTOM') {
    const widthCm = paperSize.customWidth || 21.0;
    const heightCm = paperSize.customHeight || 29.7;
    return {
      width: cmToMicrons(widthCm),
      height: cmToMicrons(heightCm)
    };
  }

  return 'A4';
};

// Fetch printer settings from backend
export const fetchPrinterSettings = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${baseURL}/master/printer-settings`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch printer settings');
    }

    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error('Error fetching printer settings:', error);
    // Return default settings if fetch fails
    return {
      printerType: 'REGULAR',
      deviceName: null,
      documentSettings: {
        pledge: { type: 'STANDARD', standardSize: 'A4' },
        receipt: { type: 'STANDARD', standardSize: 'A4' },
        ledger: { type: 'STANDARD', standardSize: 'A4' },
        pledgeBook: { type: 'STANDARD', standardSize: 'A4' },
        auctionRegister: { type: 'STANDARD', standardSize: 'A4' },
        remainderLetter: { type: 'STANDARD', standardSize: 'A4' },
        preAuctionList: { type: 'STANDARD', standardSize: 'A4' },
        saleIntimation: { type: 'STANDARD', standardSize: 'A4' },
        dayBook: { type: 'STANDARD', standardSize: 'A4' },
        journalRegister: { type: 'STANDARD', standardSize: 'A4' },
        profitLossAccount: { type: 'STANDARD', standardSize: 'A4' },
        release: { type: 'STANDARD', standardSize: 'A4' }
      }
    };
  }
};

// Fetch company data from backend (current logged in user's company)
export const fetchCompanyData = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${baseURL}/auth/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch company data');
    }

    const data = await response.json();
    console.log('📋 Raw company data response:', data);
    
    // Extract company data from user object
    // Backend returns: { status: 'success', data: { user: {...} } }
    const user = data.data?.user || data.user || data.data || data;
    console.log('📋 Extracted user object:', user);
    
    // Based on User model: companyName, address, licenseNumber, mobileNumber are directly on user
    const companyData = {
      companyName: user.companyName || 'COMPANY NAME',
      address: user.address || 'Company Address',
      mobileNumber: user.mobileNumber || 'N/A',
      licenseNumber: user.licenseNumber || 'N/A',
      pan: user.pan || null
    };
    
    console.log('📋 Processed company data:', companyData);
    return companyData;
  } catch (error) {
    console.error('❌ Error fetching company data:', error);
    // Return default company data if fetch fails
    return {
      companyName: 'COMPANY NAME',
      address: 'Company Address',
      mobileNumber: 'N/A',
      licenseNumber: 'N/A',
      pan: null
    };
  }
};

// Get paper size CSS for @page rule
export const getPaperSizeCSS = (paperSize, printerType, documentType = null, layoutTemplateVariant = null) => {
  // For dot matrix, don't set page size (continuous paper)
  if (printerType === 'DOT_MATRIX') {
    return '@page { size: auto; margin: 5mm; }';
  }

  // Force landscape for: auction register, pledge book
  const forceLandscape = documentType === 'auctionRegister' || documentType === 'pledgeBook';
  const isPledge = documentType === 'pledge';
  
  // Force portrait (pledge handled separately — Maharashtra A5 is landscape)
  const forcePortrait = documentType === 'dayBook' || 
                        documentType === 'journalRegister' || documentType === 'ledger' || 
                        documentType === 'preAuctionList' || documentType === 'remainderLetter' ||
                        documentType === 'saleIntimation';

  if (!paperSize) {
    if (forceLandscape) {
      return '@page { size: A4 landscape; margin: 10mm; }';
    }
    // For pledge without paper size, default to A4 portrait (PAWN TICKET format)
    return '@page { size: A4; margin: 10mm; }';
  }

  if (paperSize.type === 'STANDARD') {
    const sizeName = paperSize.standardSize || 'A4';
    // Pledge Book: only A4 and A3; A3 = 40cm x 22cm, 1cm margin all sides
    if (documentType === 'pledgeBook') {
      const margin = '1cm';
      if (sizeName === 'A3') return `@page { size: 40cm 22cm; margin: ${margin}; }`;
      return `@page { size: A4 landscape; margin: ${margin}; }`;
    }
    // Pre-Auction List: portrait only; A4 = regular A4, A3 = same size 22cm x 40cm (portrait), 1cm margin
    if (documentType === 'preAuctionList') {
      const margin = '1cm';
      if (sizeName === 'A3') return `@page { size: 22cm 40cm; margin: ${margin}; }`;
      return `@page { size: A4; margin: ${margin}; }`;
    }
    // Auction Register: landscape only; A4 = A4 landscape, A3 = 40cm x 22cm, 1cm margin
    if (documentType === 'auctionRegister') {
      const margin = '1cm';
      if (sizeName === 'A3') return `@page { size: 40cm 22cm; margin: ${margin}; }`;
      return `@page { size: A4 landscape; margin: ${margin}; }`;
    }
    // Release receipt: A5 money-lender = A5 portrait
    if (documentType === 'release') {
      const margin = '4mm';
      if (sizeName === 'A5' && layoutTemplateVariant === RELEASE_A5_MONEY_LENDER_VARIANT) {
        return `@page { size: A5 portrait; margin: ${margin}; }`;
      }
      return `@page { size: ${sizeName}; margin: 8mm; }`;
    }
    // Pledge: Maharashtra A5 LOAN RECEIPT = landscape; pawn ticket A5 / A4 = portrait
    if (documentType === 'pledge') {
      if (sizeName === 'A5' && layoutTemplateVariant === PLEDGE_A5_MAHARASHTRA_VARIANT) {
        /* Wider margins so the right half is not flush to the physical edge */
        return '@page { size: A5 landscape; margin: 5mm; }';
      }
      if (sizeName === 'A5' && layoutTemplateVariant === PLEDGE_A5_NO_TEAR_VARIANT) {
        return '@page { size: A5 portrait; margin: 20mm 4mm 4mm 4mm; }';
      }
      if (sizeName === 'A5' && layoutTemplateVariant === PLEDGE_A5_WITH_TEAR_VARIANT) {
        return '@page { size: A5 portrait; margin: 20mm 4mm 4mm 4mm; }';
      }
      return `@page { size: ${sizeName}; margin: 10mm; }`;
    }
    // Force portrait for daybook, journal, ledger, preAuctionList
    if (forcePortrait) {
      return `@page { size: ${sizeName}; margin: 10mm; }`;
    }
    // Pledge A5: default = PAWN TICKET + tear-off; optional Maharashtra layout via templateVariant
    // Force landscape for auction register, pledge book
    if (forceLandscape) {
      return `@page { size: ${sizeName} landscape; margin: 10mm; }`;
    }
    return `@page { size: ${sizeName}; margin: 10mm; }`;
  } else if (paperSize.type === 'CUSTOM') {
    const width = paperSize.customWidth || 21.0;
    const height = paperSize.customHeight || 29.7;
    // Pledge Book custom: 1cm margin
    if (documentType === 'pledgeBook') {
      return `@page { size: ${width}cm ${height}cm; margin: 1cm; }`;
    }
    // Pre-Auction List custom: portrait, 1cm margin
    if (documentType === 'preAuctionList') {
      return `@page { size: ${width}cm ${height}cm; margin: 1cm; }`;
    }
    // Auction Register custom: landscape, 1cm margin
    if (documentType === 'auctionRegister') {
      return `@page { size: ${width}cm ${height}cm; margin: 1cm; }`;
    }
    // Pledge: always portrait (A5 and A4 use same PAWN TICKET design)
    if (isPledge) {
      return `@page { size: ${width}cm ${height}cm; margin: 5mm; }`; // Portrait
    }
    // For landscape, swap width and height
    if (forceLandscape) {
      return `@page { size: ${height}cm ${width}cm; margin: 5mm; }`;
    }
    // For portrait, use normal width x height
    return `@page { size: ${width}cm ${height}cm; margin: 5mm; }`;
  }

  if (forceLandscape) {
    return '@page { size: A4 landscape; margin: 10mm; }';
  }
  // For pledge without paper size, default to A4 portrait
  return '@page { size: A4; margin: 10mm; }';
};

// Get the standard size name from paper settings
export const getPaperSizeName = (paperSize) => {
  if (!paperSize) return 'A4';
  if (paperSize.type === 'STANDARD') {
    return paperSize.standardSize || 'A4';
  }
  return 'CUSTOM';
};

/** Pledge Book: base font size (px) so table fits selected page. A4 landscape 10px, A3 40×22 9px, custom from area. */
export const getPledgeBookFontSize = (paperSize) => {
  if (!paperSize) return 10;
  if (paperSize.type === 'STANDARD') {
    const sizeName = paperSize.standardSize || 'A4';
    if (sizeName === 'A3') return 9;  // 40×22 cm, 1cm margin → fit more rows
    return 10;  // A4 landscape
  }
  if (paperSize.type === 'CUSTOM') {
    const w = paperSize.customWidth || 21;
    const h = paperSize.customHeight || 29.7;
    const printableArea = (w - 2) * (h - 2);  // 1cm margin each side
    const a4Printable = (29.7 - 2) * (21 - 2);  // A4 landscape with 1cm margin
    const scale = Math.sqrt(printableArea / a4Printable);
    const fontSize = Math.round(10 * scale);
    return Math.max(7, Math.min(12, fontSize));
  }
  return 10;
};

// Calculate font scale based on paper size
const getFontScale = (paperSize) => {
  if (!paperSize) return 12; // Default 12px base
  
  if (paperSize.type === 'STANDARD') {
    const sizeName = paperSize.standardSize || 'A4';
    switch (sizeName) {
      case 'A5': return 12;     // A5 readable size for ticket + tear-off
      case 'A4': return 12;     // Standard
      case 'LETTER': return 12;
      case 'LEGAL': return 13;
      case 'A3': return 14;     // Larger paper = larger font
      default: return 12;
    }
  } else if (paperSize.type === 'CUSTOM') {
    // Calculate based on paper dimensions for more accurate scaling
    const width = paperSize.customWidth || 21.0;
    const height = paperSize.customHeight || 29.7;
    const area = width * height;
    const minDimension = Math.min(width, height);
    const maxDimension = Math.max(width, height);
    
    // A5: 14.8 x 21.0 cm (area ≈ 311 cm²)
    // A4: 21.0 x 29.7 cm (area ≈ 623 cm²)
    // A3: 29.7 x 42.0 cm (area ≈ 1247 cm²)
    
    // Calculate scale factor based on area relative to A4
    const a4Area = 623; // A4 area in cm²
    const scaleFactor = area / a4Area;
    
    // Base font size for A4 is 12px
    // Scale proportionally: smaller paper = smaller font, larger paper = larger font
    let fontSize = 12 * scaleFactor;
    
    // Clamp to reasonable range (7px to 18px)
    fontSize = Math.max(7, Math.min(18, fontSize));
    
    // Round to nearest integer
    return Math.round(fontSize);
  }
  
  return 12;
};

// Calculate spacing scale based on paper size (for margins, padding, etc.)
const getSpacingScale = (paperSize) => {
  if (!paperSize) return 1.0; // Default scale factor
  
  if (paperSize.type === 'STANDARD') {
    const sizeName = paperSize.standardSize || 'A4';
    switch (sizeName) {
      case 'A5': return 0.75;   // Smaller paper = smaller spacing
      case 'A4': return 1.0;     // Standard
      case 'LETTER': return 1.0;
      case 'LEGAL': return 1.1;
      case 'A3': return 1.2;      // Larger paper = larger spacing
      default: return 1.0;
    }
  } else if (paperSize.type === 'CUSTOM') {
    // Calculate spacing scale based on paper area relative to A4
    const width = paperSize.customWidth || 21.0;
    const height = paperSize.customHeight || 29.7;
    const area = width * height;
    const a4Area = 623; // A4 area in cm²
    const scaleFactor = Math.sqrt(area / a4Area); // Use square root for more gradual scaling
    
    // Clamp to reasonable range (0.6 to 1.5)
    return Math.max(0.6, Math.min(1.5, scaleFactor));
  }
  
  return 1.0;
};

// Get print styles including paper size - content fits to paper
export const getPrintStyles = (paperSize, printerType = 'REGULAR', documentType = null, layoutTemplateVariant = null) => {
  const paperSizeCSS = getPaperSizeCSS(paperSize, printerType, documentType, layoutTemplateVariant);
  const fontSize = getFontScale(paperSize);
  const spacingScale = getSpacingScale(paperSize);
  
  return `
    ${paperSizeCSS}
    
    * {
      box-sizing: border-box;
    }
    
    @media print {
      * {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: ${
          (documentType === 'pledge' && layoutTemplateVariant === PLEDGE_A5_MAHARASHTRA_VARIANT) ||
          (documentType === 'release' && layoutTemplateVariant === RELEASE_A5_MONEY_LENDER_VARIANT)
            ? 'auto'
            : '100%'
        };
        max-height: none;
        overflow: visible;
        font-size: ${fontSize}px;
      }
      
      /* Custom size spacing adjustment - CSS variables for templates to use */
      .print-container {
        --spacing-scale: ${spacingScale};
        --base-font-size: ${fontSize}px;
      }
      
      /* Scale line-height and spacing proportionally */
      .print-container {
        line-height: calc(1.3 * var(--spacing-scale, 1));
      }
      
      .no-print {
        display: none !important;
      }
      
      .print-container {
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        overflow: hidden;
      }
      
      .print-container.print-container--maharashtra-a5,
      .print-container:has(.pledge-print-a5-maharashtra-photo-standard),
      .print-container:has(.release-print-a5-money-lender) {
        overflow: visible !important;
        height: auto !important;
        max-height: none !important;
      }

      .print-container.print-container--pledge-a5-boxed,
      .print-container:has(.pledge-print-a5-no-tear) {
        line-height: 1.3 !important;
        overflow: hidden;
        height: 100%;
      }
      .print-container:has(.pledge-print-a5-with-tear) {
        line-height: 1.3 !important;
        overflow: hidden;
        height: 100% !important;
      }
      .pledge-print-a5-no-tear {
        line-height: 1.3 !important;
        height: 100% !important;
        min-height: 100%;
        display: flex !important;
        flex-direction: column !important;
        overflow: hidden;
        page-break-inside: avoid;
        break-inside: avoid;
        box-sizing: border-box;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .pledge-print-a5-with-tear {
        line-height: 1.3 !important;
        height: 100% !important;
        min-height: 100%;
        display: flex !important;
        flex-direction: column !important;
        overflow: hidden;
        page-break-inside: avoid;
        break-inside: avoid;
        box-sizing: border-box;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .print-container.print-container--remainder-letter {
        overflow: visible !important;
        height: auto !important;
        max-height: none !important;
        font-family: 'Courier New', Courier, monospace !important;
      }

      .remainder-letter-page {
        display: flex;
        flex-direction: column;
        min-height: calc(100vh - 20mm);
        page-break-after: always;
        box-sizing: border-box;
      }

      .remainder-letter-page:last-child {
        page-break-after: auto;
      }

      .remainder-letter-body {
        flex: 1 1 auto;
      }

      .remainder-letter-inland {
        flex: 0 0 auto;
        margin-top: auto;
      }
      
      ${printerType === 'DOT_MATRIX' ? `
        /* Dot Matrix specific styles */
        .print-container {
          font-family: 'Courier New', Courier, monospace;
          font-size: 10px;
        }
        
        .pledge-print-dot-matrix {
          width: 100%;
          max-width: 80mm;
          margin: 0 auto;
        }
      ` : `
        /* Regular printer styles */
        .print-container {
          font-family: Arial, sans-serif;
        }
        
        /* A5 pawn ticket: fill page; photos in middle; tear slip at foot (no extra 1in padding) */
        .pledge-print-a5 {
          display: flex !important;
          flex-direction: column !important;
          width: 100% !important;
          height: 100% !important;
          min-height: 19.2cm !important;
          padding-bottom: 2mm !important;
          box-sizing: border-box !important;
          page-break-inside: avoid;
          overflow: visible !important;
          margin: 0 !important;
        }

        .pledge-print-a5-top {
          flex: 0 0 auto !important;
          width: 100% !important;
        }

        .pledge-print-a5-bottom {
          flex: 1 1 auto !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: flex-end !important;
          min-height: 0 !important;
          width: 100% !important;
          margin-top: auto !important;
        }
        
        /* Maharashtra A5: two-up; fill printable height so signatures can sit at bottom */
        .pledge-print-a5-maharashtra-photo-standard {
          width: 100% !important;
          height: auto !important;
          min-height: 13.7cm !important; /* A5 landscape 14.8cm − ~5mm top/bottom @page margin */
          display: flex !important;
          flex-direction: row !important;
          align-items: stretch !important;
          box-sizing: border-box !important;
          overflow: visible !important;
        }
        
        .pledge-print-a5-maharashtra-photo-standard > div {
          overflow: visible !important;
          flex: 1 1 50% !important;
          min-width: 0 !important;
          min-height: 0 !important;
          display: flex !important;
          flex-direction: column !important;
          align-self: stretch !important;
        }
        
        /* A4/Larger - single column */
        .pledge-print-large {
          width: 100%;
          height: 100%;
        }

        .release-print-a5-money-lender {
          width: 100% !important;
          height: auto !important;
          max-width: 148mm !important;
          margin: 0 auto !important;
          overflow: visible !important;
        }
      `}
    }
    
    @media screen {
      html, body {
        margin: 0;
        padding: 0;
        font-size: ${fontSize}px;
      }
      
      .print-container {
        padding: 10px;
        max-width: 900px;
        margin: 0 auto;
        background: white;
        box-shadow: 0 0 10px rgba(0,0,0,0.1);
      }
    }
  `;
};

async function withArticleMasterRate(pledgeData) {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`${baseURL}/articles`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json().catch(() => ({}));
    const master = Array.isArray(json) ? json : json?.data;
    const rate = resolveArticleMasterRate(master, pledgeData?.pledgeData || pledgeData);
    if (rate == null) return pledgeData;
    if (pledgeData?.pledgeData) {
      return {
        ...pledgeData,
        pledgeData: {
          ...pledgeData.pledgeData,
          loanDetails: { ...(pledgeData.pledgeData.loanDetails || {}), rateOfInterest: rate },
        },
      };
    }
    return {
      ...pledgeData,
      loanDetails: { ...(pledgeData?.loanDetails || {}), rateOfInterest: rate },
    };
  } catch (e) {
    console.error('Article master rate for print:', e);
    return pledgeData;
  }
}

// Main print function
async function enrichPledgeCustomer(pledgeData) {
  if (!pledgeData || typeof pledgeData !== 'object') return pledgeData;
  const customer = { ...(pledgeData.customer || {}) };
  const code = customer.code;
  const remember = (relation, relationName) => {
    const rel = String(relation || '').trim();
    const name = String(relationName || '').trim();
    if (!code || (!rel && !name)) return { rel: customer.relation || '', name: customer.relationName || '' };
    return { rel: customer.relation || rel, name: customer.relationName || name };
  };
  let relation = String(customer.relation || '').trim();
  let relationName = String(customer.relationName || '').trim();
  if (code && (!relation || !relationName)) {
    try {
      const map = JSON.parse(localStorage.getItem('girviCustomerRelation') || '{}');
      const stored = map[code];
      if (stored) {
        relation = relation || String(stored.relation || '').trim();
        relationName = relationName || String(stored.relationName || '').trim();
      }
    } catch {
      /* ignore bad local cache */
    }
  }
  if (code && (!relation || !relationName)) {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${baseURL}/ledgers?type=Customer&code=${encodeURIComponent(code)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const body = await res.json();
        const rows = Array.isArray(body?.data) ? body.data : [];
        const ledger = rows.find((row) => String(row.code) === String(code)) || rows[0];
        if (ledger) {
          relation = relation || String(ledger.relation || '').trim();
          relationName = relationName || String(ledger.relationName || '').trim();
        }
      }
    } catch (err) {
      console.error('Could not load pawner relation', err);
    }
  }
  const filled = remember(relation, relationName);
  return {
    ...pledgeData,
    customer: {
      ...customer,
      relation: filled.rel,
      relationName: filled.name
    }
  };
}

export const printDocument = async (documentType, data, TemplateComponent) => {
  try {
    if (documentType === 'pledge' && data) {
      if (data.pledgeData !== undefined) {
        data = { ...data, pledgeData: await enrichPledgeCustomer(data.pledgeData) };
      } else {
        data = await enrichPledgeCustomer(data);
      }
    }
    console.log('🖨️ printDocument called with:', { documentType, hasData: !!data, hasTemplate: !!TemplateComponent });
    const printSource = documentType === 'pledge' ? await withArticleMasterRate(data) : data;
    
    // 1. Fetch printer settings and company data in parallel
    console.log('🖨️ Fetching printer settings and company data...');
    const [settings, companyData] = await Promise.all([
      fetchPrinterSettings(),
      fetchCompanyData()
    ]);
    console.log('🖨️ Settings fetched:', { printerType: settings.printerType, hasCompanyData: !!companyData });

    const printerType = settings.printerType || 'REGULAR';
    const paperSize = settings.documentSettings[documentType];
    const paperSizeName = getPaperSizeName(paperSize);
    const layoutTemplateVariant =
      documentType === 'pledge' || documentType === 'release'
        ? paperSize?.templateVariant ?? null
        : null;

    if (!paperSize) {
      console.warn(`No paper size settings found for document type: ${documentType}`);
    }

    // 2. Get print styles (Maharashtra A5 pledge = landscape @page)
    const printStyles = getPrintStyles(paperSize, printerType, documentType, layoutTemplateVariant);
    console.log('🖨️ Print styles generated');

    // 3. Render the template component with all required props
    console.log('🖨️ Rendering template component...');
    const pledgeDataProp = printSource?.pledgeData !== undefined ? printSource.pledgeData : printSource;
    const releaseDataProp = printSource?.releaseData;

    const printContent = ReactDOMServer.renderToString(
      React.createElement(TemplateComponent, { 
        pledgeData: pledgeDataProp,
        releaseData: releaseDataProp,
        receiptData: printSource,
        ledgerData: printSource,
        pledgeBookData: printSource,
        auctionData: printSource,
        remainderData: printSource,
        preAuctionData: printSource,
        saleIntimationData: printSource,
        dayBookData: printSource,
        journalData: printSource,
        profitLossData: data,
        companyData: companyData,
        printerType: printerType,
        paperSize: paperSizeName,
        pledgeTemplateVariant: documentType === 'pledge' ? layoutTemplateVariant : null,
        releaseTemplateVariant: documentType === 'release' ? layoutTemplateVariant : null,
        documentType
      })
    );
    console.log('🖨️ Template rendered, content length:', printContent.length);

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print ${documentType}</title>
          <meta charset="UTF-8">
          <style>
            ${printStyles}
          </style>
        </head>
        <body>
          <div class="print-container${
            documentType === 'pledge' && layoutTemplateVariant === PLEDGE_A5_MAHARASHTRA_VARIANT
              ? ' print-container--maharashtra-a5'
              : ''
          }${
            documentType === 'pledge' &&
            (layoutTemplateVariant === PLEDGE_A5_NO_TEAR_VARIANT ||
              layoutTemplateVariant === PLEDGE_A5_WITH_TEAR_VARIANT)
              ? ' print-container--pledge-a5-boxed'
              : ''
          }${
            documentType === 'release' && layoutTemplateVariant === RELEASE_A5_MONEY_LENDER_VARIANT
              ? ' print-container--release-a5'
              : ''
          }${documentType === 'remainderLetter' ? ' print-container--remainder-letter' : ''}">
            ${printContent}
          </div>
          <script>
            // Note: Paper size is set via CSS @page rule
            // Please select "${paperSizeName}" ${paperSize?.type === 'STANDARD' && paperSizeName === 'A5' && layoutTemplateVariant === PLEDGE_A5_MAHARASHTRA_VARIANT ? 'Landscape' : paperSize?.type === 'STANDARD' && paperSizeName === 'A5' ? 'Portrait' : ''} in your print dialog
            console.log('Paper size should be: ${paperSizeName}');
            console.log('Company data:', ${JSON.stringify(companyData)});
          </script>
        </body>
      </html>
    `;

    // Electron desktop: silent print using saved printer (deviceName) + paper size + orientation (no dialog)
    if (window?.girviPrint?.printHtml) {
      const pageSize = getElectronPageSize(paperSize, printerType, documentType, layoutTemplateVariant);
      // Maharashtra A5 uses width×height microns (21×14.8 cm); keep landscape false so the driver is not rotated twice
      const landscape = documentType === 'auctionRegister' || documentType === 'pledgeBook';
      const result = await window.girviPrint.printHtml({
        html,
        options: {
          deviceName: settings?.deviceName || undefined,
          pageSize,
          landscape
        }
      });
      if (!result?.ok) throw new Error(result?.error || 'Electron print failed');
      return;
    }

    // Browser fallback: open print dialog
    console.log('🖨️ Opening print window...');
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print documents');
      return;
    }
    console.log('🖨️ Print window opened');

    console.log('🖨️ Writing HTML to print window...');
    console.log('🖨️ Company Data:', companyData);
    console.log('🖨️ Paper Size:', paperSizeName);
    printWindow.document.write(html);

    printWindow.document.close();
    console.log('🖨️ HTML written and document closed');

    // 6. Wait for content to load, then print
    // Use both onload and a timeout to ensure print dialog opens
    const triggerPrint = () => {
      setTimeout(() => {
        try {
          printWindow.print();
        } catch (printError) {
          console.error('Error triggering print:', printError);
          alert('Error opening print dialog. Please check your browser settings.');
        }
      }, 500);
    };

    // Try both onload and immediate timeout as fallback
    if (printWindow.document.readyState === 'complete') {
      triggerPrint();
    } else {
      printWindow.onload = triggerPrint;
      // Fallback timeout in case onload doesn't fire
      setTimeout(triggerPrint, 1000);
    }
  } catch (error) {
    console.error('Error printing document:', error);
    alert('Error printing document. Please try again.');
  }
};

// Convenience function to print pledge
export const printPledge = async (pledgeData) => {
  const PledgePrint = (await import('../components/print/templates/PledgePrint')).default;
  return printDocument('pledge', pledgeData, PledgePrint);
};

// Convenience function to print ledger
export const printLedger = async (ledgerData) => {
  const LedgerPrint = (await import('../components/print/templates/LedgerPrint')).default;
  return printDocument('ledger', ledgerData, LedgerPrint);
};

// Convenience function to print day book
export const printDayBook = async (dayBookData) => {
  const DayBookPrint = (await import('../components/print/templates/DayBookPrint')).default;
  return printDocument('dayBook', dayBookData, DayBookPrint);
};

/** Group confirmed reminder items into one letter per customer. */
export const buildRemainderLettersFromItems = (items, { noticeDate, finalDueDate } = {}) => {
  const groups = new Map();
  (items || []).forEach((item) => {
    const key = item.customerCode || item.customerName || item.ticketNo;
    if (!groups.has(key)) {
      groups.set(key, {
        customer: {
          customerCode: item.customerCode,
          customerName: item.customerName,
          firstName: item.firstName,
          lastName: item.lastName,
          fatherName: item.fatherName,
          address: item.address,
          occupation: item.occupation,
          mobileNo: item.mobileNo,
          village: item.village,
          city: item.city || item.village
        },
        pawnTickets: [],
        noticeDate,
        finalDueDate
      });
    }
    groups.get(key).pawnTickets.push({
      ticketNo: item.ticketNo,
      date: item.pledgeDate,
      pledgeDate: item.pledgeDate,
      principalAmount: item.principalAmount
    });
  });
  return Array.from(groups.values());
};

// Convenience function to print remainder letter (single or batch via letters[])
export const printRemainderLetter = async (remainderData) => {
  const RemainderLetterPrint = (await import('../components/print/templates/RemainderLetterPrint')).default;
  const payload = remainderData?.letters
    ? remainderData
    : Array.isArray(remainderData)
      ? { letters: remainderData }
      : remainderData;
  return printDocument('remainderLetter', payload, RemainderLetterPrint);
};

export const printRemainderLetters = async (letters) => {
  return printRemainderLetter({ letters });
};

// Convenience function to print journal register
export const printJournalRegister = async (journalData) => {
  const JournalRegisterPrint = (await import('../components/print/templates/JournalRegisterPrint')).default;
  return printDocument('journalRegister', journalData, JournalRegisterPrint);
};

// Convenience function to print profit & loss account
export const printProfitLossAccount = async (profitLossData) => {
  const ProfitLossAccountPrint = (await import('../components/print/templates/ProfitLossAccountPrint')).default;
  return printDocument('profitLossAccount', profitLossData, ProfitLossAccountPrint);
};

// Convenience function to print pre auction list
export const printPreAuctionList = async (preAuctionData) => {
  const PreAuctionListPrint = (await import('../components/print/templates/PreAuctionListPrint')).default;
  return printDocument('preAuctionList', preAuctionData, PreAuctionListPrint);
};

// Convenience function to print auction register
export const printAuctionRegister = async (registerData) => {
  const AuctionRegisterPrint = (await import('../components/print/templates/AuctionRegisterPrint')).default;
  return printDocument('auctionRegister', registerData, AuctionRegisterPrint);
};

// Convenience function to print receipt
export const printReceipt = async (receiptData) => {
  const ReceiptPrint = (await import('../components/print/templates/ReceiptPrint')).default;
  return printDocument('receipt', receiptData, ReceiptPrint);
};

// Helper to get template component by document type
export const getTemplateComponent = (documentType) => {
  // This will be imported from PrintTemplates
  // For now, return a placeholder
  return null;
};
